/**
 * SearchBox.js
 *
 * Componente compuesto predefinido — buscador visual con ícono.
 * De momento es solo visual (sin lógica de filtrado).
 * La interactividad se conectará cuando se implemente el SearchableGrid.
 *
 * Presets disponibles (usar en Toolbox):
 *   SEARCH_PRESETS.dark       → fondo transparente, borde blanco, ícono blanco
 *   SEARCH_PRESETS.light      → fondo blanco, borde gris, ícono gris
 *   SEARCH_PRESETS.filled     → fondo semitransparente oscuro, sin borde
 *   SEARCH_PRESETS.vibrant    → borde de color de acento, ícono de color
 *   SEARCH_PRESETS.minimal    → solo línea inferior, sin caja
 */
import React from 'react';
import { useNode } from '@craftjs/core';
import {
  TextField,
  FormControl,
  FormLabel,
  Typography,
  Divider,
  Switch,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

// ─────────────────────────────────────────────────────────────────────────────
// Estilos del Settings Panel
// ─────────────────────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  settings: {
    padding: theme.spacing(2),
  },
  sectionLabel: {
    fontWeight: 700,
    fontSize: '0.7rem',
    letterSpacing: '0.08em',
    color: '#888',
    textTransform: 'uppercase',
    margin: theme.spacing(1.5, 0, 0.5),
  },
  previewBox: {
    width: '100%',
    height: 40,
    borderRadius: 4,
    border: '1px solid #e0e0e0',
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 12,
    gap: 8,
    fontSize: '0.85rem',
    overflow: 'hidden',
  },
  switchRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
}));

// ─────────────────────────────────────────────────────────────────────────────
// Presets
// ─────────────────────────────────────────────────────────────────────────────
export const SEARCH_PRESETS = {
  // Borde blanco sobre fondo oscuro (idéntico al HTML del ejemplo)
  dark: {
    placeholder:       'Buscar por título, canalizador o categoría...',
    iconName:          'search',
    showIcon:          true,
    iconPosition:      'left',
    // Colores del input
    inputBackground:   '#2d2d3a',
    inputBorderColor:  '#ffffff',
    inputBorderStyle:  'solid',
    inputBorderWidth:  1,
    inputBorderRadius: 4,
    inputTextColor:    '#ffffff',
    inputPlaceholderColor: '#aaaaaa',
    inputFontSize:     16,
    inputPadding:      15,
    // Ícono
    iconColor:         '#ffffff',
    iconSize:          22,
    // Foco
    focusBorderColor:  '#ff5722',
    // Wrapper
    wrapperBackground: '#2d2d3a',
    wrapperBorderRadius: 4,
    wrapperPadding:    0,
    wrapperMaxWidth:   '100%',
  },

  // Fondo blanco, bordes grises — para páginas claras
  light: {
    placeholder:       'Buscar...',
    iconName:          'search',
    showIcon:          true,
    iconPosition:      'left',
    inputBackground:   '#ffffff',
    inputBorderColor:  '#cccccc',
    inputBorderStyle:  'solid',
    inputBorderWidth:  1,
    inputBorderRadius: 4,
    inputTextColor:    '#212121',
    inputPlaceholderColor: '#9e9e9e',
    inputFontSize:     15,
    inputPadding:      13,
    iconColor:         '#757575',
    iconSize:          20,
    focusBorderColor:  '#3f51b5',
    wrapperBackground: 'transparent',
    wrapperBorderRadius: 4,
    wrapperPadding:    0,
    wrapperMaxWidth:   '100%',
  },

  // Caja rellena oscura, sin borde visible
  filled: {
    placeholder:       'Buscar...',
    iconName:          'search',
    showIcon:          true,
    iconPosition:      'left',
    inputBackground:   '#2d2d3a',
    inputBorderColor:  '#2d2d3a',
    inputBorderStyle:  'solid',
    inputBorderWidth:  0,
    inputBorderRadius: 8,
    inputTextColor:    '#ffffff',
    inputPlaceholderColor: '#999999',
    inputFontSize:     15,
    inputPadding:      14,
    iconColor:         '#999999',
    iconSize:          20,
    focusBorderColor:  '#2d2d3a',
    wrapperBackground: '#2d2d3a',
    wrapperBorderRadius: 8,
    wrapperPadding:    0,
    wrapperMaxWidth:   '100%',
  },

  // Borde de acento de color, ícono naranja
  vibrant: {
    placeholder:       'Buscar por título o categoría...',
    iconName:          'search',
    showIcon:          true,
    iconPosition:      'left',
    inputBackground:   '#2d1a14',
    inputBorderColor:  '#ff5722',
    inputBorderStyle:  'solid',
    inputBorderWidth:  2,
    inputBorderRadius: 24,
    inputTextColor:    '#ffffff',
    inputPlaceholderColor: '#aaaaaa',
    inputFontSize:     15,
    inputPadding:      13,
    iconColor:         '#ff5722',
    iconSize:          22,
    focusBorderColor:  '#ff8a50',
    wrapperBackground: '#2d1a14',
    wrapperBorderRadius: 24,
    wrapperPadding:    0,
    wrapperMaxWidth:   '100%',
  },

  // Solo línea inferior, estilo minimalista
  minimal: {
    placeholder:       'Buscar...',
    iconName:          'search',
    showIcon:          true,
    iconPosition:      'left',
    inputBackground:   '#2d2d3a',
    inputBorderColor:  '#ffffff',
    inputBorderStyle:  'solid',
    inputBorderWidth:  0,
    inputBorderRadius: 0,
    inputTextColor:    '#ffffff',
    inputPlaceholderColor: '#aaaaaa',
    inputFontSize:     16,
    inputPadding:      10,
    iconColor:         '#aaaaaa',
    iconSize:          20,
    focusBorderColor:  '#ff5722',
    useBottomBorderOnly: true,
    wrapperBackground: '#2d2d3a',
    wrapperBorderRadius: 0,
    wrapperPadding:    0,
    wrapperMaxWidth:   '100%',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────
export const SearchBox = ({
  placeholder,
  iconName,
  showIcon,
  iconPosition,
  inputBackground,
  inputBorderColor,
  inputBorderStyle,
  inputBorderWidth,
  inputBorderRadius,
  inputTextColor,
  inputPlaceholderColor,
  inputFontSize,
  inputPadding,
  iconColor,
  iconSize,
  focusBorderColor,
  useBottomBorderOnly,
  wrapperBackground,
  wrapperBorderRadius,
  wrapperPadding,
  wrapperMaxWidth,
}) => {
  const { connectors: { connect, drag } } = useNode();

  const iconLeft  = showIcon && iconPosition === 'left';
  const iconRight = showIcon && iconPosition === 'right';
  const iconSizePx = iconSize || 20;
  const paddingBase = inputPadding || 13;

  // Padding del input para dejar espacio al ícono
  const inputPaddingLeft  = iconLeft  ? paddingBase + iconSizePx + 10 : paddingBase;
  const inputPaddingRight = iconRight ? paddingBase + iconSizePx + 10 : paddingBase;

  const wrapperStyle = {
    position:        'relative',
    backgroundColor: wrapperBackground || 'transparent',
    borderRadius:    wrapperBorderRadius || 0,
    padding:         wrapperPadding || 0,
    maxWidth:        wrapperMaxWidth || '100%',
    width:           '100%',
    outline:         '1px dashed transparent',
    cursor:          'pointer',
  };

  const inputStyle = {
    width:           '100%',
    paddingTop:      paddingBase,
    paddingBottom:   paddingBase,
    paddingLeft:     inputPaddingLeft,
    paddingRight:    inputPaddingRight,
    backgroundColor: inputBackground || 'transparent',
    color:           inputTextColor  || '#ffffff',
    fontSize:        inputFontSize   || 16,
    outline:         'none',
    boxSizing:       'border-box',
    fontFamily:      'inherit',
    // Borde
    ...(useBottomBorderOnly
      ? {
          border:       'none',
          borderBottom: `2px solid ${inputBorderColor || '#ffffff'}`,
          borderRadius: 0,
        }
      : {
          border:        `${inputBorderWidth || 1}px ${inputBorderStyle || 'solid'} ${inputBorderColor || '#ffffff'}`,
          borderRadius:  inputBorderRadius || 4,
        }),
  };

  const iconStyle = {
    position:   'absolute',
    top:        '50%',
    transform:  'translateY(-50%)',
    color:      iconColor || '#ffffff',
    fontSize:   iconSizePx,
    lineHeight: 1,
    pointerEvents: 'none',
    ...(iconLeft  ? { left:  paddingBase }             : {}),
    ...(iconRight ? { right: paddingBase }             : {}),
  };

  // Inyectar color de placeholder vía <style> scoped
  const placeholderCss = `
    .sb-input-${inputBorderColor?.replace('#', '')}::placeholder {
      color: ${inputPlaceholderColor || 'rgba(255,255,255,0.6)'};
    }
  `;
  const inputClass = `sb-input-${(inputBorderColor || '#fff').replace('#', '')}`;

  return (
    <div
      ref={(ref) => connect(drag(ref))}
      style={wrapperStyle}
    >
      <style>{placeholderCss}</style>

      {/* Ícono izquierdo */}
      {iconLeft && (
        <span className="material-icons" style={iconStyle}>
          {iconName || 'search'}
        </span>
      )}

      <input
        type="text"
        className={inputClass}
        placeholder={placeholder || 'Buscar...'}
        style={inputStyle}
        readOnly
      />

      {/* Ícono derecho */}
      {iconRight && (
        <span className="material-icons" style={iconStyle}>
          {iconName || 'search'}
        </span>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Settings Panel
// ─────────────────────────────────────────────────────────────────────────────
const SearchBoxSettings = () => {
  const classes = useStyles();
  const {
    actions: { setProp },
    placeholder,
    iconName,
    showIcon,
    iconPosition,
    inputBackground,
    inputBorderColor,
    inputBorderWidth,
    inputBorderRadius,
    inputTextColor,
    inputPlaceholderColor,
    inputFontSize,
    inputPadding,
    iconColor,
    iconSize,
    focusBorderColor,
    useBottomBorderOnly,
    wrapperBackground,
    wrapperBorderRadius,
    wrapperMaxWidth,
  } = useNode((node) => ({ ...node.data.props }));

  return (
    <div className={classes.settings}>

      {/* ── INPUT ───────────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Input</Typography>

      <FormControl fullWidth margin="dense">
        <FormLabel>Placeholder</FormLabel>
        <TextField
          size="small"
          value={placeholder || ''}
          onChange={(e) => setProp((p) => (p.placeholder = e.target.value))}
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Tamaño de fuente (px)</FormLabel>
        <TextField
          type="number"
          size="small"
          inputProps={{ min: 10, max: 32 }}
          value={inputFontSize || 16}
          onChange={(e) => setProp((p) => (p.inputFontSize = parseInt(e.target.value)))}
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Padding interno (px)</FormLabel>
        <TextField
          type="number"
          size="small"
          inputProps={{ min: 4, max: 40 }}
          value={inputPadding || 13}
          onChange={(e) => setProp((p) => (p.inputPadding = parseInt(e.target.value)))}
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Color de texto</FormLabel>
        <TextField
          type="color"
          size="small"
          value={inputTextColor && inputTextColor.startsWith('#') ? inputTextColor : '#ffffff'}
          onChange={(e) => setProp((p) => (p.inputTextColor = e.target.value))}
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Color placeholder</FormLabel>
        <TextField
          type="color"
          size="small"
          value={
            inputPlaceholderColor && inputPlaceholderColor.startsWith('#')
              ? inputPlaceholderColor
              : '#aaaaaa'
          }
          onChange={(e) => setProp((p) => (p.inputPlaceholderColor = e.target.value))}
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Color de fondo</FormLabel>
        <TextField
          type="color"
          size="small"
          value={
            inputBackground && inputBackground.startsWith('#')
              ? inputBackground
              : '#000000'
          }
          onChange={(e) => setProp((p) => (p.inputBackground = e.target.value))}
        />
      </FormControl>

      <Divider style={{ margin: '12px 0' }} />

      {/* ── BORDE ───────────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Borde</Typography>

      <div className={classes.switchRow}>
        <Typography variant="body2">Solo línea inferior</Typography>
        <Switch
          size="small"
          checked={!!useBottomBorderOnly}
          onChange={(e) => setProp((p) => (p.useBottomBorderOnly = e.target.checked))}
        />
      </div>

      {!useBottomBorderOnly && (
        <>
          <FormControl fullWidth margin="dense">
            <FormLabel>Grosor borde (px)</FormLabel>
            <TextField
              type="number"
              size="small"
              inputProps={{ min: 0, max: 8 }}
              value={inputBorderWidth ?? 1}
              onChange={(e) => setProp((p) => (p.inputBorderWidth = parseInt(e.target.value)))}
            />
          </FormControl>

          <FormControl fullWidth margin="dense">
            <FormLabel>Radio de bordes (px)</FormLabel>
            <TextField
              type="number"
              size="small"
              inputProps={{ min: 0, max: 50 }}
              value={inputBorderRadius ?? 4}
              onChange={(e) => setProp((p) => (p.inputBorderRadius = parseInt(e.target.value)))}
            />
          </FormControl>
        </>
      )}

      <FormControl fullWidth margin="dense">
        <FormLabel>Color del borde</FormLabel>
        <TextField
          type="color"
          size="small"
          value={inputBorderColor && inputBorderColor.startsWith('#') ? inputBorderColor : '#ffffff'}
          onChange={(e) => setProp((p) => (p.inputBorderColor = e.target.value))}
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Color borde en foco</FormLabel>
        <TextField
          type="color"
          size="small"
          value={focusBorderColor && focusBorderColor.startsWith('#') ? focusBorderColor : '#ff5722'}
          onChange={(e) => setProp((p) => (p.focusBorderColor = e.target.value))}
        />
      </FormControl>

      <Divider style={{ margin: '12px 0' }} />

      {/* ── ÍCONO ───────────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Ícono</Typography>

      <div className={classes.switchRow}>
        <Typography variant="body2">Mostrar ícono</Typography>
        <Switch
          size="small"
          checked={!!showIcon}
          onChange={(e) => setProp((p) => (p.showIcon = e.target.checked))}
        />
      </div>

      {showIcon && (
        <>
          <FormControl fullWidth margin="dense">
            <FormLabel>Nombre del ícono (Material Icons)</FormLabel>
            <TextField
              size="small"
              value={iconName || 'search'}
              onChange={(e) => setProp((p) => (p.iconName = e.target.value))}
              helperText="Ej: search, filter_list, tune"
            />
          </FormControl>

          <FormControl fullWidth margin="dense">
            <FormLabel>Posición del ícono</FormLabel>
            <TextField
              select
              size="small"
              value={iconPosition || 'left'}
              onChange={(e) => setProp((p) => (p.iconPosition = e.target.value))}
              SelectProps={{ native: true }}
            >
              <option value="left">Izquierda</option>
              <option value="right">Derecha</option>
            </TextField>
          </FormControl>

          <FormControl fullWidth margin="dense">
            <FormLabel>Color del ícono</FormLabel>
            <TextField
              type="color"
              size="small"
              value={iconColor && iconColor.startsWith('#') ? iconColor : '#ffffff'}
              onChange={(e) => setProp((p) => (p.iconColor = e.target.value))}
            />
          </FormControl>

          <FormControl fullWidth margin="dense">
            <FormLabel>Tamaño del ícono (px)</FormLabel>
            <TextField
              type="number"
              size="small"
              inputProps={{ min: 14, max: 40 }}
              value={iconSize || 20}
              onChange={(e) => setProp((p) => (p.iconSize = parseInt(e.target.value)))}
            />
          </FormControl>
        </>
      )}

      <Divider style={{ margin: '12px 0' }} />

      {/* ── CONTENEDOR ──────────────────────────────────────────── */}
      <Typography className={classes.sectionLabel}>Contenedor</Typography>

      <FormControl fullWidth margin="dense">
        <FormLabel>Color de fondo contenedor</FormLabel>
        <TextField
          type="color"
          size="small"
          value={
            wrapperBackground && wrapperBackground.startsWith('#')
              ? wrapperBackground
              : '#000000'
          }
          onChange={(e) => setProp((p) => (p.wrapperBackground = e.target.value))}
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Radio de bordes contenedor (px)</FormLabel>
        <TextField
          type="number"
          size="small"
          inputProps={{ min: 0, max: 50 }}
          value={wrapperBorderRadius ?? 4}
          onChange={(e) => setProp((p) => (p.wrapperBorderRadius = parseInt(e.target.value)))}
        />
      </FormControl>

      <FormControl fullWidth margin="dense">
        <FormLabel>Ancho máximo</FormLabel>
        <TextField
          size="small"
          value={wrapperMaxWidth || '100%'}
          onChange={(e) => setProp((p) => (p.wrapperMaxWidth = e.target.value))}
          helperText="Ej: 100%, 600px, 800px"
        />
      </FormControl>

    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// craft config — usa preset dark como default
// ─────────────────────────────────────────────────────────────────────────────
SearchBox.craft = {
  displayName: 'Buscador',
  props: { ...SEARCH_PRESETS.dark },
  related: {
    settings: SearchBoxSettings,
  },
};